'use client'

import { useState, useMemo } from 'react'
import { useStore } from '@tanstack/react-form'
import { HsTaxCBPGResponse } from '@/modules/hs/types/hs-tax.response'
import { countries as allCountries } from '@/modules/hs/constants/country.constant'
import { useCBPGCalculatorForm } from '@/modules/hs/components/HsDetail/form/useCBPGCalculatorForm'
import { cbpgCalculatorSchema } from '@/modules/hs/components/HsDetail/form/cbpg-calculator.schema'
import { createFieldValidator } from '@/modules/hs/components/HsDetail/form/valibot-validator'
import CountrySelector from '@/modules/hs/components/common/CountrySelector'

import {
  Calculator,
  GraduationCap,
  Info,
  Building2,
  Ship,
  Calendar,
  Weight,
  DollarSign,
  Pencil,
  Check,
} from 'lucide-react'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import HsTaxCBPGCalculatorResult from '@/modules/hs/components/HsDetail/tax-calculator/HsTaxCBPGCalculatorResult'

interface HsTaxCBPGCalculatorV2Props {
  hsCode: string
  taxCBPGList: HsTaxCBPGResponse[]
}

// Tỷ giá mặc định theo từng loại tiền tệ
const DEFAULT_RATES: Record<string, number> = {
  USD: 26000,
  EUR: 28000,
  CNY: 3600,
}

export default function HsTaxCBPGCalculatorV2({
  hsCode,
  taxCBPGList,
}: HsTaxCBPGCalculatorV2Props) {
  const { form, result, error, isPending } = useCBPGCalculatorForm({ hsCode })
  const [showHelpText, setShowHelpText] = useState(false)
  const [exchangeRate, setExchangeRate] = useState(26000)
  const [isEditingRate, setIsEditingRate] = useState(false)

  // Subscribe to form values
  const importCountry = useStore(
    form.store,
    (state) => state.values.importCountry,
  )
  const importDate = useStore(form.store, (state) => state.values.importDate)
  const formCOCountry = useStore(
    form.store,
    (state) => state.values.formCOCountry,
  )
  const manufactoringCompany = useStore(
    form.store,
    (state) => state.values.manufactoringCompany,
  )

  // Get unique countries with CBPG tax
  const countriesWithCBPGTax = Array.from(
    new Set(taxCBPGList.map((item) => item.countryName)),
  )

  // Sort countries: prioritize countries with tax
  const sortedCOCountries = useMemo(() => {
    const withTax = allCountries.filter((c) =>
      countriesWithCBPGTax.includes(c.value),
    )
    const withoutTax = allCountries.filter(
      (c) => !countriesWithCBPGTax.includes(c.value),
    )
    return [...withTax, ...withoutTax]
  }, [countriesWithCBPGTax])

  // Get manufacturer companies based on selected formCOCountry
  let manufacturerCompanies =
    taxCBPGList
      .find((item) => item.countryName === formCOCountry)
      ?.taxCBPGDetails?.filter((detail) => detail.type === 'Manufacturing')
      ?.map((detail) => detail.companyName) || []

  manufacturerCompanies =
    manufacturerCompanies.length > 0
      ? Array.from(new Set(manufacturerCompanies))
      : []

  // Move "Other companies" to front if exists
  if (manufacturerCompanies.includes('Other companies')) {
    manufacturerCompanies = [
      'Other companies',
      ...manufacturerCompanies.filter((item) => item !== 'Other companies'),
    ]
  }

  // Get trading companies
  let tradingCompanies =
    taxCBPGList
      .find((item) => item.countryName === formCOCountry)
      ?.taxCBPGDetails?.filter((detail) => detail.type === 'Trading')
      ?.map((detail) => detail.companyName) || []

  tradingCompanies =
    tradingCompanies.length > 0 ? Array.from(new Set(tradingCompanies)) : []

  // Move "Other companies" to front or add if not exists
  if (tradingCompanies.includes('Other companies')) {
    tradingCompanies = [
      'Other companies',
      ...tradingCompanies.filter((item) => item !== 'Other companies'),
    ]
  } else {
    tradingCompanies = ['Other companies', ...tradingCompanies]
  }

  const isShowFormCO = !!importCountry && !!importDate
  const formCOCountryHasTax = countriesWithCBPGTax.includes(formCOCountry || '')
  const isShowTradingCompany =
    formCOCountryHasTax &&
    manufactoringCompany !== undefined &&
    manufactoringCompany !== 'Other companies'

  return (
    <div className="cl-card-border mt-6">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center gap-2 mb-2">
          <Calculator className="text-[#1E40AF] h-5 w-5" />
          <h2 className="text-lg font-bold text-[#1E40AF]">
            Công cụ tính thuế CBPG
          </h2>
        </div>
      </div>

      {/* Help Text Toggle */}
      <label className="flex items-center gap-2 mb-6 cursor-pointer text-sm text-gray-600">
        <input
          type="checkbox"
          checked={showHelpText}
          onChange={(e) => setShowHelpText(e.target.checked)}
          className="w-4 h-4 rounded border-gray-300 text-[#1E40AF] focus:ring-[#1E40AF]"
        />
        <span className="flex items-center gap-1.5">
          <GraduationCap className="h-4 w-4 text-gray-400" />
          Hiển thị hướng dẫn chi tiết
        </span>
      </label>

      {/* Form */}
      <form
        onSubmit={(e) => {
          e.preventDefault()
          e.stopPropagation()
          form.handleSubmit()
        }}
      >
        <div className="space-y-5">
          {/* Row 1: C/O Country */}
          <form.Field
            name="importCountry"
            validators={createFieldValidator(
              cbpgCalculatorSchema,
              'importCountry',
            )}
          >
            {(field) => (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  C/O thể hiện hàng hóa có xuất xứ từ <span className="text-red-500">*</span>
                </label>
                <CountrySelector
                  value={field.state.value ?? ''}
                  onValueChange={(value) => field.handleChange(value)}
                  placeholder="Chọn quốc gia"
                  error={field.state.meta.errors.length > 0}
                />
                {showHelpText && (
                  <p className="mt-1.5 text-xs text-gray-500 flex items-start gap-1.5">
                    <Info className="h-3 w-3 shrink-0 mt-0.5" />
                    Các quốc gia có dấu (*) là các nước bị đánh thuế CBPG
                  </p>
                )}
                {field.state.meta.errors.length > 0 && (
                  <p className="mt-1.5 text-xs text-red-500">
                    {field.state.meta.errors[0]}
                  </p>
                )}
              </div>
            )}
          </form.Field>

          {/* Row 2: Manufacturing Company */}
          {isShowFormCO && formCOCountryHasTax && (
            <form.Field name="manufactoringCompany">
              {(field) => (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    Giấy chứng nhận công ty sản xuất <span className="text-red-500">*</span>
                  </label>
                  <Select
                    value={field.state.value}
                    onValueChange={(value) => {
                      field.handleChange(value)
                      form.setFieldValue('tradingCompany', undefined)
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Chọn công ty sản xuất" />
                    </SelectTrigger>
                    <SelectContent>
                      {manufacturerCompanies.map((company) => (
                        <SelectItem key={company} value={company}>
                          {company}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {showHelpText && (
                    <p className="mt-1.5 text-xs text-gray-500 flex items-start gap-1.5">
                      <Building2 className="h-3 w-3 shrink-0 mt-0.5" />
                      Công ty sản xuất được ghi trên giấy chứng nhận xuất xứ (C/O)
                    </p>
                  )}
                </div>
              )}
            </form.Field>
          )}

          {/* Row 3: Trading Company */}
          {isShowTradingCompany && (
            <form.Field name="tradingCompany">
              {(field) => (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    Tên công ty xuất khẩu <span className="text-red-500">*</span>
                  </label>
                  <Select
                    value={tradingCompanies.length === 0 ? '__none__' : (field.state.value || 'Other companies')}
                    onValueChange={(value) => {
                      if (value === '__none__') {
                        field.handleChange(undefined)
                      } else {
                        field.handleChange(value)
                      }
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {tradingCompanies.length === 0 ? (
                        <SelectItem value="__none__">Không có</SelectItem>
                      ) : (
                        tradingCompanies.map((company) => (
                          <SelectItem key={company} value={company}>
                            {company}
                          </SelectItem>
                        ))
                      )}
                    </SelectContent>
                  </Select>
                  {showHelpText && (
                    <p className="mt-1.5 text-xs text-gray-500 flex items-start gap-1.5">
                      <Ship className="h-3 w-3 shrink-0 mt-0.5" />
                      Công ty xuất khẩu có thể khác với công ty sản xuất
                    </p>
                  )}
                </div>
              )}
            </form.Field>
          )}

          {/* Row 4: Import Date */}
          <form.Field
            name="importDate"
            validators={createFieldValidator(
              cbpgCalculatorSchema,
              'importDate',
            )}
          >
            {(field) => (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Thời gian nhập <span className="text-red-500">*</span>
                </label>
                <Input
                  type="date"
                  value={field.state.value}
                  onChange={(e) => field.handleChange(e.target.value)}
                  onBlur={field.handleBlur}
                  className={field.state.meta.errors.length > 0 ? 'border-red-500' : ''}
                />
                {showHelpText && (
                  <p className="mt-1.5 text-xs text-gray-500 flex items-start gap-1.5">
                    <Calendar className="h-3 w-3 shrink-0 mt-0.5" />
                    Ngày dự kiến nhập khẩu hàng hóa
                  </p>
                )}
                {field.state.meta.errors.length > 0 && (
                  <p className="mt-1.5 text-xs text-red-500">
                    {field.state.meta.errors[0]}
                  </p>
                )}
              </div>
            )}
          </form.Field>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Quantity */}
            <form.Field
              name="quantity"
              validators={createFieldValidator(
                cbpgCalculatorSchema,
                'quantity',
              )}
            >
              {(field) => (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    Số lượng (kg) <span className="text-red-500">*</span>
                  </label>
                  <Input
                    type="number"
                    value={field.state.value || ''}
                    onChange={(e) => field.handleChange(Number(e.target.value))}
                    onBlur={field.handleBlur}
                    placeholder="10,000"
                    className={field.state.meta.errors.length > 0 ? 'border-red-500' : ''}
                  />
                  {showHelpText && (
                    <p className="mt-1.5 text-xs text-gray-500 flex items-start gap-1.5">
                      <Weight className="h-3 w-3 shrink-0 mt-0.5" />
                      Khối lượng hàng hóa nhập khẩu
                    </p>
                  )}
                  {field.state.meta.errors.length > 0 && (
                    <p className="mt-1.5 text-xs text-red-500">
                      {field.state.meta.errors[0]}
                    </p>
                  )}
                </div>
              )}
            </form.Field>

            {/* Unit Price & Currency */}
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Đơn giá <span className="text-red-500">*</span>
                </label>
                <div className="flex gap-2">
                  <form.Field
                    name="unitPrice"
                    validators={createFieldValidator(
                      cbpgCalculatorSchema,
                      'unitPrice',
                    )}
                  >
                    {(field) => (
                      <div className="flex-1">
                        <Input
                          type="number"
                          value={field.state.value || ''}
                          onChange={(e) => field.handleChange(Number(e.target.value))}
                          onBlur={field.handleBlur}
                          placeholder="15"
                          className={field.state.meta.errors.length > 0 ? 'border-red-500' : ''}
                        />
                        {field.state.meta.errors.length > 0 && (
                          <p className="mt-1.5 text-xs text-red-500">
                            {field.state.meta.errors[0]}
                          </p>
                        )}
                      </div>
                    )}
                  </form.Field>

                  <form.Field
                    name="currency"
                    validators={createFieldValidator(
                      cbpgCalculatorSchema,
                      'currency',
                    )}
                  >
                    {(field) => (
                      <Select
                        value={field.state.value}
                        onValueChange={(value) => field.handleChange(value)}
                      >
                        <SelectTrigger className="w-24">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="USD">USD</SelectItem>
                          <SelectItem value="VND">VND</SelectItem>
                          <SelectItem value="EUR">EUR</SelectItem>
                          <SelectItem value="CNY">CNY</SelectItem>
                        </SelectContent>
                      </Select>
                    )}
                  </form.Field>
                </div>
                {/* Tỷ giá inline edit - chỉ hiện khi chọn tiền tệ != VND */}
                <form.Subscribe selector={(state) => state.values.currency}>
                  {(currency) =>
                    currency !== 'VND' && (
                      <div className="mt-2 flex items-center gap-1.5 text-xs text-gray-500">
                        <DollarSign className="h-3 w-3 shrink-0 text-gray-400" />
                        <span className="text-gray-400">Tỷ giá:</span>
                        {isEditingRate ? (
                          <div className="flex items-center gap-1">
                            <span className="text-gray-500">1 {currency} =</span>
                            <input
                              type="number"
                              value={exchangeRate}
                              onChange={(e) => setExchangeRate(Number(e.target.value) || 1)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === 'Escape') setIsEditingRate(false)
                              }}
                              className="w-24 border border-blue-300 rounded px-2 py-0.5 text-xs focus:outline-none focus:ring-1 focus:ring-blue-500"
                              // eslint-disable-next-line jsx-a11y/no-autofocus
                              autoFocus
                            />
                            <span className="text-gray-500">VND</span>
                            <button
                              type="button"
                              onClick={() => setIsEditingRate(false)}
                              className="text-green-600 hover:text-green-700 ml-0.5"
                              title="Xác nhận"
                            >
                              <Check className="h-3.5 w-3.5" />
                            </button>
                          </div>
                        ) : (
                          <div className="flex items-center gap-1">
                            <span>1 {currency} = <strong>{exchangeRate.toLocaleString('vi-VN')}</strong> VND</span>
                            <button
                              type="button"
                              onClick={() => {
                                setExchangeRate(DEFAULT_RATES[currency] ?? 26000)
                                setIsEditingRate(true)
                              }}
                              className="text-gray-300 hover:text-[#1E40AF] ml-0.5 transition-colors"
                              title="Chỉnh tỷ giá"
                            >
                              <Pencil className="h-3 w-3" />
                            </button>
                          </div>
                        )}
                      </div>
                    )
                  }
                </form.Subscribe>
              </div>
            </div>
          </div>

          {/* Row 5: Form C/O Country (optional) */}
          {isShowFormCO && (
            <form.Field name="formCOCountry">
              {(field) => (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    C/O thể hiện hàng hóa có xuất xứ từ
                  </label>
                  <Select
                    value={field.state.value || '__no_form_co__'}
                    onValueChange={(value) => {
                      if (value === '__no_form_co__') {
                        field.handleChange(undefined)
                      } else {
                        field.handleChange(value)
                      }
                      form.setFieldValue('manufactoringCompany', undefined)
                      form.setFieldValue('tradingCompany', undefined)
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Không có Form C/O" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__no_form_co__">Không có Form C/O</SelectItem>
                      {sortedCOCountries.map((country) => (
                        <SelectItem key={country.value} value={country.value}>
                          {country.label}
                          {countriesWithCBPGTax.includes(country.value)
                            ? ' (*)'
                            : ''}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="mt-1.5 text-xs text-gray-500">
                    Các quốc gia có dấu (*) là các nước bị đánh thuế CBPG
                  </p>
                </div>
              )}
            </form.Field>
          )}

          {/* Submit Button */}
          <Button
            type="submit"
            disabled={isPending}
            className="w-full bg-[#1E40AF] hover:bg-[#1a3a9e] text-white py-3 h-12"
          >
            <Calculator className="h-4 w-4 mr-2" />
            {isPending ? 'Đang tính...' : 'Tính thuế CBPG'}
          </Button>
        </div>
      </form>

      {/* Error Display */}
      {error && (
        <div className="mt-6 bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
          <Info className="text-red-600 h-5 w-5 shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-red-800">Lỗi</p>
            <p className="text-sm text-red-700 mt-0.5">
              {error.message || 'Có lỗi xảy ra khi tính thuế. Vui lòng thử lại.'}
            </p>
          </div>
        </div>
      )}

      {/* Result Display */}
      {result && (
        <div className="mt-6">
          <HsTaxCBPGCalculatorResult
            result={result}
            quantity={form.state.values.quantity || 0}
            unitPrice={form.state.values.unitPrice || 0}
            currency={form.state.values.currency}
            exchangeRate={exchangeRate}
          />
        </div>
      )}
    </div>
  )
}
